import { useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import { LayoutDashboard, TrainFront, AlertTriangle, Settings, Power } from 'lucide-react';

interface StationMasterViewProps {
  displayName: string;
  onLogout: () => Promise<void>;
}

export default function StationMasterView({ displayName, onLogout }: StationMasterViewProps) {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'trains' | 'incidents' | 'settings'>('dashboard');

  return (
    <div className="flex h-screen w-full bg-[#020617] text-slate-300 font-sans overflow-hidden">
      {/* LEFT SIDEBAR MENU */}
      <aside className="w-[320px] bg-[#0f172a] border-r border-white/10 flex flex-col shrink-0 z-10 shadow-xl">
        {/* HEADER */}
        <div className="p-6 border-b border-white/10 flex items-center gap-4 bg-slate-900/50">
          <img src="logo.png" alt="ASTA RAIL" className="w-10 h-10 object-contain" />
          <div className="flex flex-col">
            <h1 className="text-white font-black uppercase tracking-widest leading-tight">Chef de Gare</h1>
            <p className="text-cyan-400 text-xs font-mono">{displayName}</p>
          </div>
        </div>

        {/* NAVIGATION */}
        <nav className="flex-1 p-4 flex flex-col gap-2 overflow-y-auto">
          <button 
            onClick={() => setActiveTab('dashboard')}
            className={`flex items-center gap-3 w-full p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-sm ${activeTab === 'dashboard' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
          >
            <LayoutDashboard size={20} />
            <span>Vue d'Ensemble</span>
          </button>
          
          <button 
            onClick={() => setActiveTab('trains')}
            className={`flex items-center gap-3 w-full p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-sm ${activeTab === 'trains' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
          >
            <TrainFront size={20} />
            <span>Circulations</span>
          </button>

          <button 
            onClick={() => setActiveTab('incidents')}
            className={`flex items-center gap-3 w-full p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-sm ${activeTab === 'incidents' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
          >
            <AlertTriangle size={20} />
            <span>Incidents & LTV</span>
          </button>
        </nav>

        {/* BOTTOM ACTIONS */}
        <div className="p-4 border-t border-white/10 bg-slate-900/50 space-y-2">
          <button 
            onClick={() => setActiveTab('settings')}
            className={`flex items-center gap-3 w-full p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-sm ${activeTab === 'settings' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}
          >
            <Settings size={20} />
            <span>Paramètres</span>
          </button>
          <button 
            onClick={onLogout}
            className="flex items-center gap-3 w-full p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-sm text-red-500 hover:bg-red-500/10 hover:border-red-500/20 border border-transparent"
          >
            <Power size={20} />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 flex flex-col relative">
        {/* Dynamic header depending on the active tab could go here, for now it's just the map occupying the space */}
        {activeTab === 'dashboard' && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-slate-900/90 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/10 shadow-2xl flex items-center gap-4">
             <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.6)]"></div>
             <span className="text-white font-black tracking-widest uppercase text-sm">Supervision Réseau Active</span>
          </div>
        )}

        <div className="flex-1 relative z-0">
          <MapContainer 
            center={[42.56, 8.75]} // Tattone coordinates roughly
            zoom={10} 
            className="h-full w-full"
            zoomControl={true}
          >
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
            {/* Layers like StationsMapLayer, LTVMapLayer could be added here later */}
          </MapContainer>
        </div>
      </main>
    </div>
  );
}
